import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav(loc.state?.from || "/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <Link to="/" className="flex items-center gap-2" data-testid="login-brand-link">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-[#f9f8f6] font-serif-display text-xl" style={{ background: "#163325" }}>C</div>
            <span className="font-serif-display text-2xl">Coachly</span>
          </Link>
          <div>
            <h1 className="font-serif-display text-4xl leading-none">Welcome back.</h1>
            <p className="text-[#5c5c5c] mt-2 text-sm">Sign in to see your leads and conversations.</p>
          </div>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#5c5c5c]">Email</Label>
              <Input
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 bg-white border-[#e5e2da] focus-visible:ring-[#163325]"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-[#5c5c5c]">Password</Label>
              <Input
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 bg-white border-[#e5e2da] focus-visible:ring-[#163325]"
              />
            </div>
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="login-submit-btn"
            className="w-full rounded-full bg-[#163325] hover:bg-[#0e2419] text-[#f9f8f6] h-11"
          >
            {loading ? "Signing in…" : "Sign in"}
          </Button>
          <p className="text-sm text-[#5c5c5c]">
            No account?{" "}
            <Link to="/plans" className="text-[#163325] underline underline-offset-4" data-testid="login-signup-link">
              Start trial
            </Link>
          </p>
        </form>
      </div>
      <div
        className="hidden lg:block relative"
        style={{ background: "linear-gradient(180deg, #163325 0%, #0e2419 100%)" }}
      >
        <img
          src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMGNvYWNoJTIwc21pbGluZyUyMHBvcnRyYWl0fGVufDB8fHx8MTc4NTcxMzQ1OHww&ixlib=rb-4.1.0&q=85"
          alt="Coach at work"
          className="absolute inset-0 w-full h-full object-cover opacity-70"
        />
        <div className="absolute inset-0 flex items-end p-10 text-[#f9f8f6]">
          <div className="max-w-md">
            <p className="font-serif-display text-3xl leading-tight">
              "I booked 11 discovery calls the first week after switching."
            </p>
            <p className="text-sm mt-3 opacity-80">— Renée K., high-ticket business coach</p>
          </div>
        </div>
      </div>
    </div>
  );
}
